import React, { useState, useCallback } from 'react';
import SprintCardCommentsPopover from './SprintCardCommentsPopover';

// Minimal Sprint card for kanban usage; prefer the full SprintCard when available
const Chip = ({ children, className = '' }) => (
  <span className={`inline-flex items-center rounded-full border border-succession-border px-2 py-0.5 text-xs capitalize ${className}`}>
    {children}
  </span>
);

const priorityClasses = (priority) => {
  const p = String(priority || '').toLowerCase();
  switch (p) {
    case 'high':
      return 'bg-succession-error-bg text-succession-error border-succession-error';
    case 'medium':
      return 'bg-succession-warning-bg text-succession-warning border-succession-warning';
    case 'low':
      return 'bg-succession-success-bg text-succession-success border-succession-success';
    default:
      return 'bg-succession-bg-secondary text-succession-text-secondary';
  }
};

const Card = ({ item, isDragging, onClick, onSelect, isSelected, draggable, onDragStart, onDragEnd, onKeyDown, enableSelection = false, onCommentAdded }) => {
  const [popoverJustClosed, setPopoverJustClosed] = React.useState(false);

  const handleCardClick = (e) => {
    // Prevent card click if popover was just closed
    if (popoverJustClosed) {
      setPopoverJustClosed(false);
      return;
    }
    if (onClick) {
      onClick();
    }
  };

  return (
  <div
    className={`bg-succession-bg-primary border border-succession-border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
      isDragging ? 'opacity-50 rotate-2 shadow-lg' : ''
    } ${isSelected ? 'ring-2 ring-succession-focus-ring border-succession-border' : ''}`}
    draggable={draggable}
    onDragStart={onDragStart}
    onDragEnd={onDragEnd}
    onKeyDown={onKeyDown}
    tabIndex={draggable ? 0 : -1}
    onClick={handleCardClick}
  >
    <div className="flex items-start justify-between mb-2">
      <h3 className="font-semibold text-succession-text-primary text-sm leading-tight mr-2">{item.title}</h3>
      <div className="flex items-center gap-2">
        <SprintCardCommentsPopover 
          item={item} 
          onCommentAdded={() => {
            if (onCommentAdded) onCommentAdded();
            // Set flag to prevent card click after comment is added
            setPopoverJustClosed(true);
            setTimeout(() => setPopoverJustClosed(false), 200);
          }} 
        />
      {enableSelection && onSelect && (
        <input
          type="checkbox"
          checked={!!isSelected}
          onChange={(e) => onSelect(e.target.checked)}
          className="rounded border-succession-border text-succession-primary focus:ring-[var(--succession-focus-ring)]"
          onClick={(e) => e.stopPropagation()}
        />
      )}
      </div>
    </div>
    {/* Badges */}
    <div className="flex items-center gap-2 mb-3 flex-wrap">
      {item.domain && (
        <Chip className={`${item.domain === 'personal' ? 'bg-succession-primary-10 text-succession-primary' : 'bg-succession-success-bg text-succession-success'}`}>{item.domain}</Chip>
      )}
      {item.cepaCategory && (
        <Chip className="bg-succession-bg-secondary text-succession-text-secondary">{item.cepaCategory}</Chip>
      )}
      {item.priority && (
        <Chip className={priorityClasses(item.priority)}>{String(item.priority).toLowerCase()}</Chip>
      )}
    </div>
    {item.dueAt && (
      <div className="text-xs text-succession-text-secondary">Due {new Date(item.dueAt).toLocaleDateString()}</div>
    )}
    {item.coach_review_status === 'pending' && (
      <div className="mt-2 text-xs text-succession-warning bg-succession-warning-bg px-2 py-1 rounded">
        ⏳ Pending Coach Review
      </div>
    )}
    {item.coach_review_status === 'rejected' && (
      <div className="mt-2 text-xs text-succession-error bg-succession-error-bg px-2 py-1 rounded">
        ❌ Rejected - Please revise
      </div>
    )}
  </div>
  );
};

const SprintKanbanBoard = ({
  items = [],
  onItemUpdate,
  onItemClick,
  onRemoveFromSprint,
  onBulkMarkCompleted,
  isReadOnly = false,
  className = '',
  maxPerColumn,
  seeMoreLinks,
  columnMinHeightClass = 'min-h-96',
  hideEmptyHint = false,
  enableSelection = false,
  isCoachLed = false, // New prop to indicate coach-led sprint
  onCommentAdded, // Callback when a comment is added to refresh data
}) => {
  const [selected, setSelected] = useState({ open: [], in_progress: [], pending_review: [], completed: [] });
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  // Helper to filter items by status and coach review status
  const byStatus = (status, coachReviewStatus = null) => {
    return items.filter((i) => {
      // Normalize status to lowercase for comparison
      const itemStatus = String(i.status).toLowerCase();
      const targetStatus = String(status).toLowerCase();
      if (itemStatus !== targetStatus) return false;
      if (coachReviewStatus !== null) {
        const itemReviewStatus = i.coach_review_status ? String(i.coach_review_status).toLowerCase() : null;
        const targetReviewStatus = String(coachReviewStatus).toLowerCase();
        return itemReviewStatus === targetReviewStatus;
      }
      return true;
    });
  };

  // Build columns based on whether it's coach-led
  const columns = isCoachLed
    ? [
        { id: 'open', title: 'To Do', itemsAll: byStatus('open') },
        { id: 'in_progress', title: 'In Progress', itemsAll: byStatus('in_progress') },
        { id: 'pending_review', title: 'Pending Review', itemsAll: byStatus('completed', 'pending') },
        { id: 'completed', title: 'Done', itemsAll: byStatus('completed').filter(i => i.coach_review_status === 'approved' || !i.coach_review_status) },
      ]
    : [
        { id: 'open', title: 'To Do', itemsAll: byStatus('open') },
        { id: 'in_progress', title: 'In Progress', itemsAll: byStatus('in_progress') },
        { id: 'completed', title: 'Done', itemsAll: byStatus('completed') },
      ];

  const handleItemSelect = useCallback((columnId, itemId, isChecked) => {
    setSelected((prev) => ({
      ...prev,
      [columnId]: isChecked ? [...prev[columnId], itemId] : prev[columnId].filter((id) => id !== itemId),
    }));
  }, []);

  const getSelected = useCallback((columnId) => selected[columnId] || [], [selected]);

  const onDragStart = (item) => (e) => {
    if (isReadOnly) return;
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (columnId) => (e) => {
    if (isReadOnly || !draggedItem) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };
  const onDragLeave = (e) => {
    // Only clear dragOverColumn if we're actually leaving the drop zone
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverColumn(null);
    }
  };
  const onDrop = (columnId) => async (e) => {
    if (isReadOnly || !draggedItem) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOverColumn(null);
    
    // Check if we're actually changing columns
    const currentStatus = draggedItem.status;
    const currentReviewStatus = draggedItem.coach_review_status;
    
    // Determine if this is a valid move
    let shouldUpdate = false;
    let updates = {};
    
    if (columnId === 'pending_review') {
      // Moving to pending review: must be completed with pending review status
      if (currentStatus !== 'completed' || currentReviewStatus !== 'pending') {
        shouldUpdate = true;
        updates = { status: 'completed', coach_review_status: 'pending' };
      }
    } else if (columnId === 'completed') {
      // Moving to done: must be completed and approved (or no review required)
      if (currentStatus !== 'completed' || (currentReviewStatus && currentReviewStatus !== 'approved')) {
        shouldUpdate = true;
        // If coach-led, we need to check if review is required
        if (isCoachLed && currentReviewStatus === 'pending') {
          // Can't move pending items directly to done - they need approval first
          setDraggedItem(null);
          return;
        }
        updates = { status: 'completed', coach_review_status: currentReviewStatus || undefined };
      }
    } else {
      // Moving to other columns (open, in_progress)
      if (currentStatus !== columnId) {
        shouldUpdate = true;
        updates = { status: columnId };
        // Don't set coach_review_status to null - just omit it and let backend handle clearing
        // The backend will clear coach_review_status when status changes away from completed
      }
    }
    
    if (shouldUpdate && onItemUpdate) {
      try {
        await onItemUpdate(draggedItem.id, updates);
      } catch (err) {
        console.error('Error updating item:', err);
      }
    }
    
    setDraggedItem(null);
  };

  const allSelected = Object.values(selected).flat();

  return (
    <div className={`flex-1 overflow-hidden ${className}`}>
      <div className="h-full flex gap-6 p-6">
        {columns.map((col) => {
          const fullCount = col.itemsAll.length;
          const displayItems = typeof maxPerColumn === 'number' ? col.itemsAll.slice(0, maxPerColumn) : col.itemsAll;
          const isTruncated = typeof maxPerColumn === 'number' ? fullCount > maxPerColumn : false;
          const seeMoreHref = seeMoreLinks && seeMoreLinks[col.id];
          return (
          <div key={col.id} className="flex-1 flex flex-col min-w-80 max-w-96">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-succession-text-primary">
                {col.title} <span className="text-sm text-succession-text-secondary">{fullCount}</span>
              </h3>
              {enableSelection && !isReadOnly && fullCount > 0 && (
                <div className="text-xs text-succession-text-secondary">
                  {getSelected(col.id).length} selected
                </div>
              )}
            </div>
            <div
              className={`flex-1 ${columnMinHeightClass} rounded-lg border-2 border-dashed transition-colors ${
                dragOverColumn === col.id ? 'border-succession-primary bg-succession-primary-10' : 'border-succession-border bg-succession-bg-secondary'
              } ${fullCount === 0 ? (hideEmptyHint ? 'flex flex-col' : 'flex flex-col items-center justify-center') : 'flex flex-col'}`}
              onDragOver={onDragOver(col.id)}
              onDragLeave={onDragLeave}
              onDrop={onDrop(col.id)}
            >
              {fullCount === 0 ? (
                hideEmptyHint ? (
                  <div className="p-4 space-y-3">
                    {[1,2,3].map((i) => (
                      <div key={`sk-${i}`} className="bg-succession-bg-primary border border-succession-border rounded-lg p-4 animate-pulse">
                        <div className="h-3 w-2/3 bg-succession-hover-bg rounded mb-2" />
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-16 bg-succession-hover-bg rounded" />
                          <div className="h-2 w-10 bg-succession-hover-bg rounded" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-succession-text-muted">Drag items here</div>
                )
              ) : (
                <>
                <div className="p-4 space-y-3 overflow-y-auto flex-1">
                  {displayItems.map((it) => {
                    // Map snake_case to camelCase for SprintCardCommentsPopover
                    const itemWithComments = {
                      ...it,
                      commentCount: it.comment_count || 0,
                      unreadCommentCount: it.unread_comment_count || 0,
                    };
                    return (
                    <Card
                      key={it.id}
                      item={itemWithComments}
                      isDragging={draggedItem?.id === it.id}
                      isSelected={enableSelection ? getSelected(col.id).includes(it.id) : false}
                      onSelect={
                        enableSelection && !isReadOnly ? (checked) => handleItemSelect(col.id, it.id, checked) : undefined
                      }
                      onClick={() => onItemClick && onItemClick(it)}
                      draggable={!isReadOnly}
                      onDragStart={onDragStart(it)}
                      onDragEnd={(e) => {
                        // Only clear if we're not dropping (drop event will handle it)
                        if (e.dataTransfer.dropEffect === 'none') {
                          setDraggedItem(null);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (isReadOnly) return;
                        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                          e.preventDefault();
                          const idx = columns.findIndex((c) => c.id === col.id);
                          const next = e.key === 'ArrowRight' ? idx + 1 : idx - 1;
                          if (next >= 0 && next < columns.length) {
                            const nextCol = columns[next];
                            // For pending_review column, we need to set status to completed and coach_review_status to pending
                            if (nextCol.id === 'pending_review' && onItemUpdate) {
                              onItemUpdate(it.id, { status: 'completed', coach_review_status: 'pending' });
                            } else if (onItemUpdate) {
                              onItemUpdate(it.id, { status: nextCol.id });
                            }
                          }
                        }
                      }}
                      enableSelection={enableSelection}
                      onCommentAdded={onCommentAdded}
                    />
                  )})}
                </div>
                {isTruncated && seeMoreHref && (
                  <div className="border-t border-succession-border px-4 py-2">
                    <a href={seeMoreHref} className="block text-center text-xs text-succession-primary hover:text-succession-primary-hover">See more…</a>
                  </div>
                )}
                </>
              )}
            </div>
          </div>
        )})}
      </div>
      {enableSelection && !isReadOnly && allSelected.length > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-succession-bg-primary border border-succession-border rounded-lg shadow-lg p-4">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-succession-text-primary">{allSelected.length} items selected</span>
            {onBulkMarkCompleted && (
              <button className="px-3 py-1 text-sm border rounded-md" onClick={() => onBulkMarkCompleted(allSelected)}>
                Mark Completed
              </button>
            )}
            {onRemoveFromSprint && (
              <button className="px-3 py-1 text-sm border rounded-md" onClick={() => onRemoveFromSprint(allSelected)}>
                Remove from Sprint
              </button>
            )}
            <button className="px-3 py-1 text-sm" onClick={() => setSelected({ open: [], in_progress: [], completed: [] })}>
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SprintKanbanBoard;


